import React, { useState, useCallback } from "react";
import {
  Box, Paper, Typography, TextField, Button, Stack, Table, TableBody,
  TableRow, TableCell, CircularProgress, Divider, Alert,
} from "@mui/material";
import api from "../services/api";
import { todayISO } from "../utils/date";

const today = () => todayISO();
const firstOfMonth = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const fmt = (n) => {
  const v = Number(n) || 0;
  const s = Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v < 0 ? `(${s})` : s;
};

/* One "Account …… amount" line */
function Line({ label, amount, indent = 3, bold, color }) {
  return (
    <TableRow>
      <TableCell sx={{ pl: indent, border: 0, py: 0.4, fontWeight: bold ? 700 : 400, color }}>{label}</TableCell>
      <TableCell align="right" sx={{ border: 0, py: 0.4, fontWeight: bold ? 700 : 400, color, fontVariantNumeric: "tabular-nums" }}>
        {fmt(amount)}
      </TableCell>
    </TableRow>
  );
}

/* A section header row (e.g. "Income") */
function SectionHead({ label }) {
  return (
    <TableRow>
      <TableCell colSpan={2} sx={{ border: 0, pt: 1.5, pb: 0.5, fontWeight: 700, fontSize: 15, color: "text.primary" }}>
        {label}
      </TableCell>
    </TableRow>
  );
}

/* A subtotal / total row with a top border */
function TotalRow({ label, amount, strong }) {
  return (
    <TableRow>
      <TableCell sx={{ pl: 3, borderTop: "1px solid", borderColor: "divider", py: 0.6, fontWeight: strong ? 800 : 700 }}>
        {label}
      </TableCell>
      <TableCell align="right" sx={{ borderTop: "1px solid", borderColor: "divider", py: 0.6, fontWeight: strong ? 800 : 700, fontVariantNumeric: "tabular-nums" }}>
        {fmt(amount)}
      </TableCell>
    </TableRow>
  );
}

export default function ProfitLoss() {
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = useCallback(() => {
    if (!from || !to) { setError("Pick a date range"); return; }
    setLoading(true); setError(null);
    api.get("/accounting/profit-loss", { params: { from, to } })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.error || "Failed to build Profit & Loss"))
      .finally(() => setLoading(false));
  }, [from, to]);

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>Profit &amp; Loss</Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Accrual basis — invoices count on their invoice date, whether or not paid yet.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
          <TextField type="date" size="small" label="From" InputLabelProps={{ shrink: true }} value={from} onChange={(e) => setFrom(e.target.value)} />
          <TextField type="date" size="small" label="To" InputLabelProps={{ shrink: true }} value={to} onChange={(e) => setTo(e.target.value)} />
          <Button variant="contained" onClick={run} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : "Run report"}
          </Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && !loading && (
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ textAlign: "center", mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Profit &amp; Loss</Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {data.fromDate} to {data.toDate}
            </Typography>
          </Box>
          <Divider sx={{ mb: 1 }} />

          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ maxWidth: 720, mx: "auto" }}>
              <TableBody>
                {/* Income */}
                <SectionHead label="Income" />
                {data.income.length === 0 && <Line label="No income in this period" amount={0} color="text.secondary" />}
                {data.income.map((r) => <Line key={`i-${r.account}`} label={r.account} amount={r.amount} />)}
                <TotalRow label="Total Income" amount={data.incomeTotal} />

                {/* COGS — periodic: Beginning Inventory + Purchases − Ending Inventory */}
                <SectionHead label="Cost of Goods Sold" />
                <Line label="Beginning Inventory" amount={data.beginningInventory} />
                {(data.purchases || []).map((r) => <Line key={`c-${r.account}`} label={r.account} amount={r.amount} />)}
                {(data.purchases || []).length === 0 && <Line label="No purchases in this period" amount={0} color="text.secondary" />}
                <Line label="Less: Ending Inventory" amount={-Math.abs(data.endingInventory)} />
                <TotalRow label="Total Cost of Goods Sold" amount={data.cogsTotal} />

                {/* Gross profit */}
                <TotalRow label="Gross Profit" amount={data.grossProfit} strong />

                {/* Expenses */}
                <SectionHead label="Expenses" />
                {data.expenses.length === 0 && <Line label="No expenses recorded in Daily Cash" amount={0} color="text.secondary" />}
                {data.expenses.map((r) => <Line key={`e-${r.account}`} label={r.account} amount={r.amount} />)}
                <TotalRow label="Total Expenses" amount={data.expenseTotal} />

                {/* Net income */}
                <TableRow>
                  <TableCell sx={{ pl: 3, borderTop: "2px solid", borderColor: "text.primary", py: 1, fontWeight: 800, fontSize: 16 }}>
                    Net Income
                  </TableCell>
                  <TableCell align="right" sx={{
                    borderTop: "2px solid", borderColor: "text.primary", py: 1, fontWeight: 800, fontSize: 16,
                    fontVariantNumeric: "tabular-nums",
                    color: data.netIncome < 0 ? "error.main" : "success.main",
                  }}>
                    {fmt(data.netIncome)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>

          <Typography variant="caption" sx={{ display: "block", mt: 2, color: "text.secondary" }}>
            Income = sale invoices · Cost of Goods Sold = Beginning Inventory + Purchases − Ending Inventory
            (periodic method, inventory valued at weighted-average cost) · Expenses = Daily Cash expense lines.
            Revenue and purchases are grouped into the same accounts as your QuickBooks export mapping.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
